import { DataType } from '@doss/prisma/types'
import { z } from 'zod'
import { defineSystemEvent } from './definition.js'
import {
  systemEventChangeSchema,
  systemEventChangesSchema,
  systemEventFailureDataSchema,
} from './primitives.js'

const tableData = z.strictObject({ table_id: z.uuid(), name: z.string().optional() })
const tableTarget = tableData.pick({ table_id: true })
const tableClearData = tableTarget.extend({
  parent_row_id: z.uuid().nullable(),
  // Counts committed deletes, including descendant tables for a whole-table clear.
  rows_deleted: z.number().int().nonnegative(),
})
// Display names ride along where the writer has them, so a feed needs no lookup of an entity
// that may since have been renamed or deleted. parent_row_id: null is a top-level row; absent
// means the writer did not know.
const rowData = z.strictObject({
  table_id: z.uuid(),
  row_id: z.uuid(),
  parent_row_id: z.uuid().nullable().optional(),
  row_name: z.string().optional(),
})
const rowTarget = rowData.pick({ table_id: true, row_id: true })
const cellTarget = rowTarget.extend({ column_id: z.uuid() })
const columnData = z.strictObject({
  table_id: z.uuid(),
  column_id: z.uuid(),
  name: z.string().optional(),
  data_type: z.enum(DataType).optional(),
})
const columnTarget = columnData.pick({ table_id: true, column_id: true })
const tabData = z.strictObject({
  table_id: z.uuid(),
  tab_id: z.uuid(),
  name: z.string().optional(),
})
const tabTarget = tabData.pick({ table_id: true, tab_id: true })
const csvData = z.strictObject({
  table_id: z.uuid(),
  mode: z.enum(['insert', 'update']),
  rows_created: z.number().int().nonnegative().optional(),
  rows_updated: z.number().int().nonnegative().optional(),
})
// A formula recompute is its own execution. Its batch ID is the execution ID that its start, its
// outcome, the requests merged into it and the cells it writes all share: the recompute's changes
// are the UPDATE_TABLE_CELL events under that execution ID, so its outcome carries no count.
const formulaRecomputeData = tableTarget.extend({ execution_id: z.string().min(1) })
const formulaRecomputeOutcomeData = formulaRecomputeData.extend({
  requests_merged: z.number().int().positive(),
})

export const tableSystemEvents = {
  tableCreateSuccess: defineSystemEvent({
    type: 'CREATE_TABLE',
    status: 'SUCCESS',
    entityTypes: ['TABLE'],
    data: tableData,
  }),
  tableCreateFailed: defineSystemEvent({
    type: 'CREATE_TABLE',
    status: 'FAIL',
    entityTypes: ['TABLE'],
    data: systemEventFailureDataSchema.extend({ name: z.string().optional() }),
  }),
  tableUpdateSuccess: defineSystemEvent({
    type: 'UPDATE_TABLE',
    status: 'SUCCESS',
    entityTypes: ['TABLE'],
    data: tableTarget.extend({ changes: systemEventChangesSchema }),
  }),
  tableUpdateFailed: defineSystemEvent({
    type: 'UPDATE_TABLE',
    status: 'FAIL',
    entityTypes: ['TABLE'],
    data: tableTarget.extend(systemEventFailureDataSchema.shape),
  }),
  tableDeleteSuccess: defineSystemEvent({
    type: 'DELETE_TABLE',
    status: 'SUCCESS',
    entityTypes: ['TABLE'],
    data: tableData,
  }),
  tableDeleteFailed: defineSystemEvent({
    type: 'DELETE_TABLE',
    status: 'FAIL',
    entityTypes: ['TABLE'],
    data: tableTarget.extend(systemEventFailureDataSchema.shape),
  }),
  tableClearSuccess: defineSystemEvent({
    type: 'CLEAR_TABLE',
    status: 'SUCCESS',
    entityTypes: ['TABLE'],
    data: tableClearData,
  }),
  tableClearFailed: defineSystemEvent({
    type: 'CLEAR_TABLE',
    status: 'FAIL',
    entityTypes: ['TABLE'],
    data: tableClearData.extend(systemEventFailureDataSchema.shape),
  }),
  tableRowCreateSuccess: defineSystemEvent({
    type: 'CREATE_TABLE_ROW',
    status: 'SUCCESS',
    entityTypes: ['TABLE_ROW'],
    data: rowData,
  }),
  tableRowCreateFailed: defineSystemEvent({
    type: 'CREATE_TABLE_ROW',
    status: 'FAIL',
    entityTypes: ['TABLE_ROW', 'TABLE'],
    data: tableTarget.extend(systemEventFailureDataSchema.shape),
  }),
  // Row updates are recorded per cell, so column permissions can filter them.
  tableCellUpdateSuccess: defineSystemEvent({
    type: 'UPDATE_TABLE_CELL',
    status: 'SUCCESS',
    entityTypes: ['TABLE_CELL'],
    data: cellTarget.extend({
      ...systemEventChangeSchema.shape,
      parent_row_id: z.uuid().nullable().optional(),
      column_name: z.string().optional(),
    }),
  }),
  tableCellUpdateFailed: defineSystemEvent({
    type: 'UPDATE_TABLE_CELL',
    status: 'FAIL',
    entityTypes: ['TABLE_CELL', 'TABLE_ROW'],
    data: cellTarget.partial({ column_id: true }).extend(systemEventFailureDataSchema.shape),
  }),
  tableRowDeleteSuccess: defineSystemEvent({
    type: 'DELETE_TABLE_ROW',
    status: 'SUCCESS',
    entityTypes: ['TABLE_ROW'],
    data: rowData,
  }),
  tableRowDeleteFailed: defineSystemEvent({
    type: 'DELETE_TABLE_ROW',
    status: 'FAIL',
    entityTypes: ['TABLE_ROW'],
    data: rowTarget.extend(systemEventFailureDataSchema.shape),
  }),
  // A failed upsert can't say which rows it would have created or updated, so it targets the table.
  // A successful one is recorded by its row and cell events.
  tableRowsUpsertFailed: defineSystemEvent({
    type: 'UPSERT_TABLE_ROWS',
    status: 'FAIL',
    entityTypes: ['TABLE'],
    data: tableTarget.extend(systemEventFailureDataSchema.shape),
  }),
  tableColumnCreateSuccess: defineSystemEvent({
    type: 'CREATE_TABLE_COLUMN',
    status: 'SUCCESS',
    entityTypes: ['TABLE_COLUMN'],
    data: columnData,
  }),
  tableColumnCreateFailed: defineSystemEvent({
    type: 'CREATE_TABLE_COLUMN',
    status: 'FAIL',
    entityTypes: ['TABLE_COLUMN', 'TABLE'],
    data: tableTarget.extend(systemEventFailureDataSchema.shape),
  }),
  tableColumnUpdateSuccess: defineSystemEvent({
    type: 'UPDATE_TABLE_COLUMN',
    status: 'SUCCESS',
    entityTypes: ['TABLE_COLUMN'],
    data: columnTarget.extend({ name: z.string().optional(), changes: systemEventChangesSchema }),
  }),
  tableColumnUpdateFailed: defineSystemEvent({
    type: 'UPDATE_TABLE_COLUMN',
    status: 'FAIL',
    entityTypes: ['TABLE_COLUMN'],
    data: columnTarget.extend(systemEventFailureDataSchema.shape),
  }),
  tableColumnDeleteSuccess: defineSystemEvent({
    type: 'DELETE_TABLE_COLUMN',
    status: 'SUCCESS',
    entityTypes: ['TABLE_COLUMN'],
    data: columnData,
  }),
  tableColumnDeleteFailed: defineSystemEvent({
    type: 'DELETE_TABLE_COLUMN',
    status: 'FAIL',
    entityTypes: ['TABLE_COLUMN'],
    data: columnTarget.extend(systemEventFailureDataSchema.shape),
  }),
  csvUploadStarted: defineSystemEvent({
    type: 'UPLOAD_CSV_STARTED',
    status: 'SUCCESS',
    entityTypes: ['TABLE'],
    data: csvData.pick({ table_id: true, mode: true }),
  }),
  csvUploadSuccess: defineSystemEvent({
    type: 'UPLOAD_CSV',
    status: 'SUCCESS',
    entityTypes: ['TABLE'],
    data: csvData,
  }),
  csvUploadFailed: defineSystemEvent({
    type: 'UPLOAD_CSV',
    status: 'FAIL',
    entityTypes: ['TABLE'],
    data: csvData.extend(systemEventFailureDataSchema.shape),
  }),
  formulaRecomputeStarted: defineSystemEvent({
    type: 'RECOMPUTE_FORMULAS_STARTED',
    status: 'SUCCESS',
    entityTypes: ['TABLE'],
    data: formulaRecomputeData,
  }),
  formulaRecomputeSuccess: defineSystemEvent({
    type: 'RECOMPUTE_FORMULAS',
    status: 'SUCCESS',
    entityTypes: ['TABLE'],
    data: formulaRecomputeOutcomeData,
  }),
  formulaRecomputeFailed: defineSystemEvent({
    type: 'RECOMPUTE_FORMULAS',
    status: 'FAIL',
    entityTypes: ['TABLE'],
    data: formulaRecomputeOutcomeData.extend(systemEventFailureDataSchema.shape),
  }),
  // One per request a recompute of two or more serves, recorded under that request's own context,
  // so its trace reaches the recompute through the shared execution ID.
  formulaRecomputeMerged: defineSystemEvent({
    type: 'MERGE_FORMULA_RECOMPUTE',
    status: 'SUCCESS',
    entityTypes: ['TABLE'],
    data: formulaRecomputeData,
  }),
  tableTabCreateSuccess: defineSystemEvent({
    type: 'CREATE_TABLE_TAB',
    status: 'SUCCESS',
    entityTypes: ['TABLE_TAB'],
    data: tabData,
  }),
  tableTabCreateFailed: defineSystemEvent({
    type: 'CREATE_TABLE_TAB',
    status: 'FAIL',
    entityTypes: ['TABLE_TAB', 'TABLE'],
    data: tableTarget.extend(systemEventFailureDataSchema.shape),
  }),
  tableTabUpdateSuccess: defineSystemEvent({
    type: 'UPDATE_TABLE_TAB',
    status: 'SUCCESS',
    entityTypes: ['TABLE_TAB'],
    data: tabTarget.extend({ name: z.string().optional(), changes: systemEventChangesSchema }),
  }),
  tableTabUpdateFailed: defineSystemEvent({
    type: 'UPDATE_TABLE_TAB',
    status: 'FAIL',
    entityTypes: ['TABLE_TAB'],
    data: tabTarget.extend(systemEventFailureDataSchema.shape),
  }),
  tableTabDeleteSuccess: defineSystemEvent({
    type: 'DELETE_TABLE_TAB',
    status: 'SUCCESS',
    entityTypes: ['TABLE_TAB'],
    data: tabData,
  }),
  tableTabDeleteFailed: defineSystemEvent({
    type: 'DELETE_TABLE_TAB',
    status: 'FAIL',
    entityTypes: ['TABLE_TAB'],
    data: tabTarget.extend(systemEventFailureDataSchema.shape),
  }),
}
